import React, { FC, useMemo, useState } from 'react';
import { Checkbox, Table, TableColumnType } from 'antd';
import { EmptyState } from 'common/components/EmptyState';
import { ISupplyShort } from 'common/models';
import { AuthHooks, UserHooks } from 'common/hooks';
import { ReactComponent as ArrowDownShortIcon } from 'app/assets/images/redesign/arrow-down-short.svg';
import { ReactComponent as ArrowUpShortIcon } from 'app/assets/images/redesign/arrow-up-short.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { RequestSupplyChangeDeliveryDateModal } from 'entities/Request/components/RequestSupplyChangeDeliveryDateModal';
import {
  getRequestDataSourceSupplies,
  getRequestStatus,
  getRequestSuppliesCheckAll,
  getRequestSuppliesStatusFilterOptions,
  groupRequestSuppliesByDeliveryDateAndStatus,
  renderRequestSupplyListRecords,
} from 'entities/Request/Request.helper';
import { IRequest, IRequestFilters, IRequestSupplyGroupItem } from 'entities/Request/Request.models';
import { ERequestSupplyGroupType } from 'entities/Request/Request.enum';
import { ESupplyStatus } from 'entities/Supply/Supply.enum';
import { excludeSuppliesByStatuses } from 'entities/Supply/Supply.helper';
import { changeSupplyStatus, selectIsSupplyLoading, splitSupply } from 'entities/Supply/Supply.slice';
import { selectIsBasketLoading } from 'entities/Basket/Basket.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  isViewMode: boolean;
  request: IRequest;
  filters: IRequestFilters;
  selectedSupplies: ISupplyShort[];
  marginBottomOffsetPx: number;
  onRowClick: (id: number) => void;
  onFiltersChange: (value: IRequestFilters) => void;
  onSelectedSuppliesChange: (value: ISupplyShort[]) => void;
}

export const RequestSupplyList: FC<IComponentProps> = ({
  isViewMode,
  request,
  filters,
  selectedSupplies,
  marginBottomOffsetPx,
  onRowClick,
  onFiltersChange,
  onSelectedSuppliesChange,
}) => {
  const dispatch = useAppDispatch();
  // basket
  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  // supply
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [supply, setSupply] = useState<ISupplyShort | null>(null);
  const [openStatusFilterDropdown, setOpenStatusFilterDropdown] = useState<boolean>(false);
  const [openRequestSupplyChangeDeliveryDateModal, setOpenRequestSupplyChangeDeliveryDateModal] = useState<boolean>(false);
  const { isSeller } = AuthHooks.useIsSeller();
  const { isAccountAdmin, isSellerManager, isManager } = UserHooks.useCurrentUserRole();

  const selectedSuppliesIds = selectedSupplies.map((supply) => supply.id);
  const { isCreatedRequest, isProcessingRequest } = getRequestStatus(request.status);
  const rowSelection =
    (!isViewMode && isManager && isCreatedRequest) || (isSeller && (isAccountAdmin || isSellerManager) && isProcessingRequest);
  const dataSource = useMemo(() => groupRequestSuppliesByDeliveryDateAndStatus(request, filters), [request, filters]);
  const dataSourceSupplies = useMemo(() => getRequestDataSourceSupplies(dataSource), [dataSource]);
  const checkAll = useMemo(() => {
    return getRequestSuppliesCheckAll(dataSourceSupplies, selectedSupplies);
  }, [dataSourceSupplies, selectedSupplies]);
  const statusFilterOptions = useMemo(() => getRequestSuppliesStatusFilterOptions(request), [request]);

  const onCheckAll = (checked: boolean) => {
    if (checked) {
      const newSelectedSupplies = excludeSuppliesByStatuses(dataSourceSupplies, [
        ESupplyStatus.Delivered,
        ESupplyStatus.Cancelled,
      ]);

      onSelectedSuppliesChange(newSelectedSupplies);
    } else {
      onSelectedSuppliesChange([]);
    }
  };

  const onCheckGroup = (records: ISupplyShort[], checked: boolean) => {
    const filteredSupplies = excludeSuppliesByStatuses(records, [ESupplyStatus.Cancelled]);
    const suppliesIds = filteredSupplies.map((supply) => supply.id);

    if (checked) {
      onSelectedSuppliesChange([...new Set([...selectedSupplies, ...filteredSupplies])]);
    } else {
      const newSelectedSupplies = selectedSupplies.filter((supply) => !suppliesIds.includes(supply.id));

      onSelectedSuppliesChange(newSelectedSupplies);
    }
  };

  const onCheckRow = (record: ISupplyShort, checked: boolean) => {
    if (checked) {
      onSelectedSuppliesChange([...selectedSupplies, record]);
    } else {
      const newSelectedSupplies = selectedSupplies.filter((supply) => supply.id !== record.id);

      onSelectedSuppliesChange(newSelectedSupplies);
    }
  };

  const onSupplyStatusChange = (id: number, value: ESupplyStatus) => {
    dispatch(
      changeSupplyStatus({
        requestId: request.id,
        ids: [id],
        status: value,
        onSuccess: () => {
          const newSelectedSupplies = selectedSupplies.filter((supply) => supply.id !== id);

          onSelectedSuppliesChange(newSelectedSupplies);
        },
      }),
    );
  };

  const onAssignDeliveryDateClick = (record: ISupplyShort) => {
    setSupply(record);
    setOpenRequestSupplyChangeDeliveryDateModal(true);
  };

  const onRequestSupplyChangeDeliveryDateModalApplyClick = (deliveryDate: string, count: number, onSuccess: () => void) => {
    if (supply) {
      dispatch(
        splitSupply({
          supplyId: supply.id,
          deliveryDate,
          count,
          onSuccess: () => {
            const newSelectedSupplies = selectedSupplies.filter((item) => item.id !== supply.id);

            onSelectedSuppliesChange(newSelectedSupplies);
            onSuccess();
          },
        }),
      );
    }
  };

  const getStatusColumnFilterProps = (): TableColumnType<IRequestSupplyGroupItem> => {
    return {
      title: isCreatedRequest || isProcessingRequest ? null : 'Статус заказа',
      filterDropdown: () => {
        return (
          <div className="redesign">
            <Checkbox.Group
              className="checkbox-group-with-divider"
              options={statusFilterOptions}
              onChange={(value) => onFiltersChange({ ...filters, statuses: value as string[] })}
            />
          </div>
        );
      },
      filterIcon: () => {
        return isCreatedRequest || isProcessingRequest ? (
          <div className="table-filter-column">
            <span className="text-tag-accent color-dark-grey">Статус заказа</span>

            {openStatusFilterDropdown ? (
              <ArrowUpShortIcon className="icon-arrow-up-short-dark-grey" />
            ) : (
              <ArrowDownShortIcon className="icon-arrow-down-short-dark-grey" />
            )}
          </div>
        ) : null;
      },
      onFilterDropdownOpenChange: setOpenStatusFilterDropdown,
    };
  };

  const columns = renderRequestSupplyListRecords(
    isViewMode,
    request,
    isSeller,
    currentUser,
    isSupplyLoading,
    onSupplyStatusChange,
    onAssignDeliveryDateClick,
    getStatusColumnFilterProps,
  );

  return (
    <>
      <div className="request-supply-list">
        <Table
          className="mb-2 request-supply-list__table"
          size="middle"
          pagination={false}
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          onRow={(record) => {
            return {
              style: { cursor: 'pointer' },
              onClick: () => {
                if (record.data) {
                  onRowClick(record.data.id);
                }
              },
            };
          }}
          rowSelection={
            rowSelection
              ? {
                  selectedRowKeys: selectedSuppliesIds,
                  columnTitle: <Checkbox checked={checkAll} onChange={(e) => onCheckAll(e.target.checked)} />,
                  columnWidth: 36,
                  onSelect: (record, selected) => onCheckRow(record.data as ISupplyShort, selected),
                  getCheckboxProps: (record) => {
                    return {
                      disabled:
                        record.data?.status === ESupplyStatus.Delivered || record.data?.status === ESupplyStatus.Cancelled,
                    };
                  },
                }
              : undefined
          }
          components={{
            body: {
              row: ({ children, ...props }: any) => {
                const row = props['data-row-key'];
                const record = dataSource.find((item) => item.key === row);

                if (record?.type === ERequestSupplyGroupType.Date) {
                  const groupSupplies = request.supplies.filter((supply) => record.ids?.includes(supply.id));
                  const filteredGroupSupplies = excludeSuppliesByStatuses(groupSupplies, [ESupplyStatus.Cancelled]);
                  const checkedGroupSupplies = groupSupplies.filter((supply) => selectedSuppliesIds.includes(supply.id));
                  const checkedGroup =
                    !!checkedGroupSupplies.length && checkedGroupSupplies.length === filteredGroupSupplies.length;
                  const disabled = groupSupplies.every((supply) => {
                    return supply.status === ESupplyStatus.Delivered || supply.status === ESupplyStatus.Cancelled;
                  });

                  return (
                    <tr className="request-supply-list__table-group-row">
                      {rowSelection && (
                        <td>
                          <Checkbox
                            checked={checkedGroup}
                            onChange={(e) => onCheckGroup(groupSupplies, e.target.checked)}
                            disabled={disabled}
                          />
                        </td>
                      )}

                      <td colSpan={columns.length}>
                        <span className="text-controls-accent">{record.label}</span>
                      </td>
                    </tr>
                  );
                }

                return <tr {...props}>{children}</tr>;
              },
            },
          }}
          locale={{
            emptyText: <EmptyState title="Ничего не найдено" description="Попробуйте изменить условия или сбросить фильтры." />,
          }}
          loading={isBasketLoading}
        />

        <div
          className="text-tag-accent color-dark-grey request-supply-list__supply-count"
          style={{ marginBottom: `calc(${marginBottomOffsetPx}px + 72px)` }}
        >
          {`Всего ${dataSourceSupplies.length} позиций`}
        </div>
      </div>

      <RequestSupplyChangeDeliveryDateModal
        open={openRequestSupplyChangeDeliveryDateModal}
        supplies={supply ? [supply] : []}
        canSplitSupply
        loading={isSupplyLoading}
        onClose={() => setOpenRequestSupplyChangeDeliveryDateModal(false)}
        onApply={onRequestSupplyChangeDeliveryDateModalApplyClick}
      />
    </>
  );
};
